import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  defaultExpanded,
  isBookContextCollapsed,
  isChapterContextCollapsed,
} from '@/lib/bible-context-persistence';
import styles from './BibleContextBlock.module.css';

interface BibleContextBlockProps {
  kind: 'book' | 'chapter';
  bookSlug: string;
  bookName: string;
  chapter: number;
  contextText: string | null;
  state: 'loading' | 'ready' | 'error';
  /**
   * Called when the user's toggle action crosses the default boundary
   * (collapse-against-default writes true; expand-from-persisted-collapse
   * writes false). The parent owns the persistence helper call so this
   * component stays decoupled from storage and reusable across kinds.
   */
  onPersistedChange: (collapsed: boolean) => void;
}

/**
 * Inline collapsible disclosure that surfaces a book or chapter context
 * paragraph above the verse content in the Bible reader. The parent
 * (BibleChapter) is expected to key this component on a route-derived
 * string (e.g. `${bookSlug}-${chapter}`) so it remounts on navigation
 * and the useState initializer re-reads persistence on every chapter
 * load. No reset effect required.
 *
 * Accessibility: the disclosure is a button inside an h2 inside a
 * <section aria-labelledby>. The button's accessible name is the
 * visible text (no aria-label), and aria-expanded carries state. The
 * disclosed body uses the `hidden` attribute so screen readers ignore
 * collapsed content without unmounting the DOM.
 */
export default function BibleContextBlock({
  kind,
  bookSlug,
  bookName,
  chapter,
  contextText,
  state,
  onPersistedChange,
}: BibleContextBlockProps) {
  const { t } = useTranslation();

  const defaultExpandedValue = defaultExpanded(chapter, kind);
  const persistedCollapsed =
    kind === 'book'
      ? isBookContextCollapsed(bookSlug)
      : isChapterContextCollapsed(bookSlug, chapter);
  const [expanded, setExpanded] = useState(
    defaultExpandedValue && !persistedCollapsed,
  );

  const handleToggle = useCallback(() => {
    setExpanded((prev) => {
      const next = !prev;
      // Persistence policy: only the default-against-user-collapsed
      // transitions touch storage. Default-collapsed blocks toggle
      // per-visit only.
      if (defaultExpandedValue) {
        onPersistedChange(!next);
      }
      return next;
    });
  }, [defaultExpandedValue, onPersistedChange]);

  if (state === 'ready' && !contextText) return null;
  if (state === 'error') return null;

  const headingId =
    kind === 'book'
      ? `bible-book-context-heading-${bookSlug}`
      : `bible-chapter-context-heading-${bookSlug}-${chapter}`;
  const bodyId =
    kind === 'book'
      ? `bible-book-context-body-${bookSlug}`
      : `bible-chapter-context-body-${bookSlug}-${chapter}`;
  // f.1.c only emits kind='book'. The chapter branch is wired now so
  // f.2 lands cleanly when the chapterContextHeader i18n keys ship.
  const labelKey =
    kind === 'book' ? 'bible.bookContextHeader' : 'bible.chapterContextHeader';
  const labelArgs =
    kind === 'book' ? { book: bookName } : { book: bookName, chapter };

  return (
    <section className={styles.section} aria-labelledby={headingId}>
      <h2 id={headingId} className={styles.heading}>
        <button
          type="button"
          className={styles.toggle}
          aria-expanded={expanded}
          aria-controls={bodyId}
          onClick={handleToggle}
        >
          <span className={styles.label}>{t(labelKey, labelArgs)}</span>
          <Chevron expanded={expanded} />
        </button>
      </h2>
      <div id={bodyId} hidden={!expanded} className={styles.body}>
        {state === 'loading' && (
          <p className={styles.bodyText}>{t('bible.bookContextLoading')}</p>
        )}
        {state === 'ready' && contextText && (
          <p className={`${styles.bodyText} ${styles.fadeIn}`}>{contextText}</p>
        )}
      </div>
    </section>
  );
}

interface ChevronProps {
  expanded: boolean;
}

function Chevron({ expanded }: ChevronProps) {
  return (
    <svg
      className={styles.chevron}
      data-expanded={expanded || undefined}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}
